import { useDynamicPageContext } from '@/contexts/DynamicPageContext';
import { Lead, PartialLead } from '@superfunnel/types';
import Button from '@superfunnel/ui-core/v2/Button';
import { Tabs, TabsList, TabsTrigger } from '@superfunnel/ui-core/v2/Tabs';
import { collection, query, where } from 'firebase/firestore';
import { Import } from 'iconoir-react';
import { Plus } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LeadsTable } from '../../components/LeadsTable';
import { PartialLeadsTable } from '../../components/PartialLeadsTable';
import { UserContext } from '../../contexts/UserContext';
import ExportLeadsPopover from '../../popovers/ExportLeadsPopover';
import { emptyConverter } from '../../utils';

type LeadViewType = 'leads' | 'partialLeads';

export default function ManageDynamicPageLeads() {
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useDynamicPageContext();
  const { slug } = useParams();
  const navigate = useNavigate();
  const [viewType, setViewType] = useState<LeadViewType>('leads');

  const leadsQuery = useMemo(() => {
    if (!selectedAccount || !selectedDynamicPage) return;
    return query(
      collection(selectedAccount.ref, 'leads').withConverter(
        emptyConverter<Lead>(),
      ),
      where('campaignId', '==', selectedDynamicPage.id),
    );
  }, [selectedAccount, selectedDynamicPage]);

  const partialLeadsQuery = useMemo(() => {
    if (!selectedAccount || !selectedDynamicPage) return;
    return query(
      collection(selectedAccount.ref, 'partialLeads').withConverter(
        emptyConverter<PartialLead>(),
      ),
      where('campaignId', '==', selectedDynamicPage.id),
    );
  }, [selectedAccount, selectedDynamicPage]);

  return (
    <div className="absolute inset-0 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base-900 text-lg font-semibold">Leads</h2>
        <div className="flex items-center gap-2.5">
          <Tabs
            value={viewType}
            onValueChange={(value) => setViewType(value as LeadViewType)}
          >
            <TabsList>
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="partialLeads">Partial Leads</TabsTrigger>
            </TabsList>
          </Tabs>
          <ExportLeadsPopover
            defaultDynamicPageIds={
              selectedDynamicPage ? [selectedDynamicPage.id] : undefined
            }
            defaultLeadType={viewType}
          >
            <Button
              className="rounded-2lg w-10"
              variant="outline"
              color="white"
            >
              <Import className="h-5 w-5" />
            </Button>
          </ExportLeadsPopover>
          <Button
            className="rounded-2lg font-inter h-10 shrink-0 gap-2.5 tracking-[0.01em] has-[>svg]:px-4.5"
            onClick={() => navigate(`/workspace/${slug}/settings/integrations`)}
          >
            <Plus className="mr-2 size-4" />
            Add Integration
          </Button>
        </div>
      </div>
      <div className="relative flex-1">
        {viewType === 'leads' && leadsQuery && (
          <LeadsTable query={leadsQuery} />
        )}
        {viewType === 'partialLeads' && partialLeadsQuery && (
          <PartialLeadsTable query={partialLeadsQuery} />
        )}
      </div>
    </div>
  );
}
